<template>
  <div class="image-container" ref="imageContainer">
    <img
      ref="image"
      src="@/assets/13_11zon.webp"
      alt="Imagen de ejemplo"
      class="cover-image"
      @load="onImageLoad"
    >

    <div class="overlay"></div>
    <div ref="darkOverlay" class="darkness-layer"></div>

    <div ref="textRef" class="text-overlay">
      <div class="firma text-line"></div>
      <div class="nombre-editorial text-line">
        <span class="inicial-script">K</span><span class="resto-nombre">AROL ALONDRA</span>
      </div>
      <div class="apellidos text-line"> ROJAS IBARRA</div>
    </div>
  </div>
</template>

<script>
import { onMounted, onUnmounted, ref } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export default {
  name: 'CoverImageComponent',
  setup() {
    const image = ref(null)
    const textRef = ref(null)
    const imageContainer = ref(null)
    const darkOverlay = ref(null)
    let ctx = null

    const initAnimations = () => {
      if (ctx) ctx.revert()

      ctx = gsap.context(() => {
        // Timeline coordinada con el scroll
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: imageContainer.value,
            // 'top 75%' asegura que la imagen ya entró claramente a la pantalla en móviles
            start: 'top 75%', 
            end: 'top 10%',   
            scrub: 1.8, // Inercia alta para movimiento suave y fluido
            invalidateOnRefresh: true
          }
        })

        // 1. Revelado suave de la imagen
        tl.fromTo(
          image.value,
          { opacity: 0.1, scale: 1.15 },
          { opacity: 1, scale: 1, ease: 'power1.out' }
        )
        .fromTo(
          darkOverlay.value,
          { opacity: 0.7 },
          { opacity: 0, ease: 'power1.out' },
          '<'
        )

        // 2. NUEVA ANIMACIÓN DE TEXTO: Revelado progresivo por capas
        // Seleccionamos las líneas dentro del contenedor para un escalonado suave
        const textLines = textRef.value.querySelectorAll('.text-line')

        tl.fromTo(
          textLines,
          { 
            y: 40, 
            opacity: 0,
            clipPath: 'inset(100% 0% 0% 0%)' // Máscara que abre de abajo hacia arriba
          },
          { 
            y: 0, 
            opacity: 1,
            clipPath: 'inset(0% 0% 0% 0%)',
            stagger: 0.15, // Cada línea entra con un pequeño retardo
            ease: 'power2.out'
          },
          'top 65%' // Inicia la entrada del texto cuando la imagen está asentada
        )

        // 3. Paralaje continuo
        gsap.to(image.value, {
          yPercent: 8,
          ease: 'none',
          scrollTrigger: {
            trigger: imageContainer.value,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        })

      }, imageContainer.value)
    }

    const onImageLoad = () => {
      ScrollTrigger.refresh()
    }

    onMounted(() => {
      initAnimations()
    })

    onUnmounted(() => {
      if (ctx) ctx.revert()
    })

    return { textRef, image, imageContainer, darkOverlay, onImageLoad }
  }
}
</script><style scoped>
.image-container {
  width: 100%;
  height: 45vh; 
  height: 45svh; 
  margin: 0; /* Removidos los márgenes negativos que causaban desfases */
  overflow: hidden; /* Evita que la escala o el paralaje generen barras de scroll extra */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Cambiado de 'contain' a 'cover' para llenar todo el espacio sin franjas */
  position: absolute;
  top: 0;
  left: 0;
  will-change: transform, opacity;
  transform: translateZ(0);
}

.darkness-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  z-index: 1;
  pointer-events: none;
  will-change: opacity;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0; /* Removido margin-bottom negativo */
  background: linear-gradient(
    to bottom,
    rgba(0,0,0,0.45) 0%,
    rgba(0,0,0,0.15) 35%,
    rgba(0,0,0,0.4) 65%,
    rgba(0,0,0,0.85) 100%
  );
  z-index: 2;
  pointer-events: none;
}

/* TEXTO POSICIONADO */
.text-overlay {
  position: absolute;
  bottom: 40px; /* Ajustado para mantener una distancia limpia sin márgenes negativos */
  width: 100%;
  text-align: center;
  color: #fff;
  z-index: 10;
  padding: 0 20px;
  box-sizing: border-box;
}

/* Clase comodín para agrupar las líneas que se animan */
.text-line {
  will-change: transform, opacity, clip-path;
  transform: translateZ(0);
}

.firma {
  font-size: 19px;
  font-family: 'Josefin Sans', sans-serif;
  font-weight: 400;
  margin: 0;
}

.nombre-editorial {
  position: relative;
  line-height: 0.85;
  margin-top: 10px;
  margin-bottom: 15px;
  color: #ffffff;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
}

.inicial-script {
  font-family: 'Pinyon Script', 'Great Vibes', cursive;
  font-size: clamp(70px, 20vw, 105px);
  font-weight: 400;
  display: inline-block;
  transform: translateY(14px) translateX(6px);
  margin-right: 12px;
}

.resto-nombre {
  font-family: 'Cormorant Garamond', 'Cinzel', serif;
  font-size: clamp(28px, 8vw, 48px);
  font-weight: 300;
  letter-spacing: 4px;
}

.apellidos {
  font-family: 'Cinzel', serif;
  font-size: clamp(10px, 3vw, 12px);
  font-weight: 400;
  letter-spacing: 8px;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 12px;
  text-align: center;
}
</style>